import { AddToPhotos, CreateNewFolder, Remove } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GetAppIcon from '@mui/icons-material/GetApp';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { CSSProperties, ReactElement } from 'react';

// API and Path constants
export const API_PATH = '/api';
export const HOME_BREADCRUMB = 'library';
export const HOME_PATH = `/${HOME_BREADCRUMB}`;
export const MEDIA_PATH = `${API_PATH}/media`;
export const SHARED_MEDIA_PATH = `${API_PATH}/shared/media`;
export const EMAIL_PATH = `${API_PATH}/email`;
export const METADATA_FORM_DETAIL_PATH = '/media/metadata_form_detail';
export const CHUNKED_UPLOAD_PATH = `${API_PATH}/media/chunked_upload`;
export const CHUNKED_UPLOAD_COMPLETE = `${API_PATH}/media/chunked_upload_complete`;
export const LOGIN_LOGO_API = `${API_PATH}/logo/login_logo`;
export const HEADER_LOGO_API = `${API_PATH}/logo/header_logo`;
export const FAVICON_LOGO_API = `${API_PATH}/logo/favicon`;
export const LINK_INFO_DOWNLOADABLE = 'downloadable';
export const GALLERY_CTYPE = 'GALLERY';
export const PLAYLIST_CTYPE = 'PLAYLIST';

// Search and filter constants
export const SEARCH_PARAM = 'term';
export const FILTER_PARAM = 'filters';
export const HAS_THE_WORDS = 'include';
export const DOESNT_HAVE_THE_WORDS = 'exclude';
export const HAS_THE_PHRASE = 'include';
export const DOESNT_HAVE_THE_PHRASE = 'exclude';

export const PAGE = 'page';
export const START = 'start';
export const START_DATE = 'startdate';
export const END_DATE = 'enddate';
export const INCLUDE_TRANSCRIPT = 'transcript';
export const SORT = 'sort';

// Mode constants
export const VISUAL_MODE = 'VISUAL-MODE';
export const INSERT_MODE = 'INSERT-MODE';

// Size and layout constants
export const NAV_BAR_HEIGHT = 56;
export const DEFAULT_INFINITE_SCROLL_SIZE = 24;
export const DEFAULT_INFINITE_SCROLL_FETCH_SIZE = 24;
export const DEFAULT_SIZE = 24;
export const GALLERY_SIZE = 96;
// OpenSearch index.max_result_window — server-side cap on `from + size`.
//
// IMPORTANT: This value is duplicated in the backend at
// `backend/library/gql_schema/query/opensearch.py` (also `OS_RESULT_WINDOW = 10000`).
// Both sides must agree, since both clamp pagination requests against this bound.
// If the OpenSearch index setting is ever raised, update both locations together.
export const OS_RESULT_WINDOW = 10000;

// Backend cap from `restrict_pagination_size(..., limit=100, ...)` in
// `backend/library/gql_schema/query/opensearch.py:604`. The frontend rejects
// `?size > MAX_PAGE_SIZE` so the UI never computes page math for a size the
// backend would silently clamp.
export const MAX_PAGE_SIZE = 100;
export const SIZE = 'size';
export const LAYOUT = 'layout';
export const GRID = 'grid';
export const INLINE = 'inline';
export const TWOUP = 'twoup';
export const GALLERY_ONLY = 'gallery_only';

export const DENSITY = 'density';
export const COMPACT = 'compact';
export const COMFORTABLE = 'comfortable';
export const SPACIOUS = 'spacious';

export const COLLECTION_TYPE = 'collection_type';

// Local storage keys for scope-based layout persistence
export const LOCAL_STORAGE_KEYS = {
  LIBRARY_LAYOUT: 'akomi.library.layout',
  GALLERY_LAYOUT: 'akomi.gallery.layout',
  LIBRARY_FILTERS: 'akomi.library.filters',
  LEFT_DRAWER_WIDTH: 'akomi.sidebar.leftDrawerWidth',
  LIBRARY_DENSITY: 'akomi.library.density',
  GALLERY_DENSITY: 'akomi.gallery.density',
  LISTVIEW_DENSITY: 'akomi.listview.density',
} as const;

// Helper function for dynamic gallery filter keys
export const getGalleryFilterKey = (galleryId: string): string => `akomi.gallery.${galleryId}.filters`;

// Filter state version for future migrations
export const FILTER_STATE_VERSION = 1;

// Filter persistence prefix for clearing all gallery filters
export const GALLERY_FILTER_PREFIX = 'akomi.gallery.';

// View context identifiers for scope-aware behaviors
export const VIEW_CONTEXT = {
  LIBRARY: 'library',
  GALLERY: 'gallery',
} as const;

// Time constants
export const ONE_HOUR = 3600000;
export const ONE_DAY = ONE_HOUR * 24;
export const ONE_WEEK = ONE_DAY * 7;
export const ONE_MONTH = ONE_WEEK * 4;
export const EMPTY_STRING = '';

// Format constants
export const PROXY_FORMAT_SLUG = 'akomi_format-proxy';
export const THUMBNAIL_FORMAT_SLUG = 'akomi_format-thumbnail';
export const HIRES_FORMAT_SLUG = 'akomi_format-hires';

// Field type constants
export const MULTISELECT_FIELD_TYPE = 'MS';
export const CHECKBOX_FIELD_TYPE = 'CB';
export const HTML_FIELD_TYPE = 'HT';
export const NATIVE_FIELD = 'NATIVE';
export const CUSTOM_FIELD = 'CUSTOM';
export const FORMAT_FIELD = 'FORMAT';

// Collection and asset constants
export const COLLECTION = 'collection';
export const ASSET = 'asset';
export const SELECTED_ITEMS = 'selectedItems';
export const LIBRARY_COLLECTION_NAME = 'Library';
export const INBOX_COLLECTION_NAME = 'Inbox';

// System and theme constants
export const SYSTEM = 'SYSTEM';
export const DEFAULT_THEME_NAME = 'default';
export const NATIVE = 'NATIVE';
export const CUSTOM = 'CUSTOM';
export const FORMAT = 'FORMAT';

// Action type constants
export const HIRES_REQUEST = 'Request Hires';
export const MARKER_SUBCLIP_ACTION_TYPE = 'MSCLIP';
export const HIRES_ACTION_TYPE = 'hires';

// Logo and site configuration constants
export const LOGIN_LOGO_OPTION_KEY = 'login_logo';
export const HEADER_LOGO_OPTION_KEY = 'header_logo';
export const FAV_ICON_OPTION_KEY = 'favicon';
export const SITE_TITLE = 'site_title';
export const ENABLE_OAUTH = 'oauth_enable';
export const ALLOW_SELF_REGISTRATION = 'allow_self_registration';
export const DEFAULT_PERMISSIONS_GROUP = 'default_permissions_group';
export const DEFAULT_SSO_PERMISSIONS_GROUP = 'default_sso_permissions_group';

// Enable or Disable downloading markers menu item
export const DOWNLOAD_MARKERS = 'download_markers';

// SMTP and email constants
export const SMTP_HOST = 'email_smtp';
export const SMTP_PORT = 'email_smtp_port';
export const SMTP_USER = 'email_smtp_user';
export const SMTP_PASSWORD = 'email_smtp_password';
export const EMAIL_FROM_ADDRESS = 'email_from_address';

// Miscellaneous configuration constants
export const LICENSE = 'license';
export const CUSTOM_LOGIN_MESSAGE = 'login_message';
export const THEME_PRIMARY_COLOR = 'theme_primary_color';
export const THEME_SECONDARY_COLOR = 'theme_secondary_color';
export const DROPFRAME_FRAMERATES = ['23.976', '29.97', '59.94'] as const;
export const DROPFRAME_DELIMITER = ';';
export const JOBS_LIST_ENABLE = 'jobs_table_enable';
export const UPLOAD_PAGE_ENABLE = 'upload_page_enable';

// User role types
export interface UserRole {
  label: string;
  value: 'ADM' | 'MGR' | 'USR';
}

export const USER_ROLES: UserRole[] = [
  { label: 'Admin', value: 'ADM' },
  { label: 'Manager', value: 'MGR' },
  { label: 'User', value: 'USR' },
];

export const NOT_ADMIN_ROLES: UserRole[] = [
  { label: 'Manager', value: 'MGR' },
  { label: 'User', value: 'USR' },
];

// Job state color constants
export const JOB_STATE_COLORS: Record<string, string> = {
  complete: '#00ab66',
  error: '#dd4837',
  in_progress: '#5070ff',
  retrying: '#c2981a',
  presigning_complete: '#026a81',
  presigning: '#e28c1f',
  creating_sublip: '#9966cc',
  pre_flight: '#d064a3',
};

// Data type constants
export const TEXT = 'TEXT';
export const INTEGER = 'INTEGER';
export const DataTypes = { TEXT } as const;

// Panel view model component names
export const INFO_PANEL_VIEW_MODEL_COMP_NAME = 'MediaCardHorizontalInfo';
export const DETAILS_PANEL_VIEW_MODEL_COMP_NAME = 'MediaCardHorizontalDetails';
export const TECH_PANEL_VIEW_MODEL_COMP_NAME = 'TechPanel';

// Gallery view component names
export const VIEW_GALLERY_CARD_META = 'GalleryCardMeta';
export const VIEW_GALLERY_PAGE = 'GalleryPage';

// External link constants
export const STREAMWELL_LINK = 'https://demo.streamwell.net/embed.php?publickey=ZGY5NDBiMDAxODc1Ng%22';
export const SCREENING_ROOM_WINDOW_TITLE = 'Akomi V3 Screening Room';

// Style constants
export const lineFontStyles: CSSProperties = { fontSize: 16, lineHeight: 2 };

export const DEFAULT_SSO_PERMISSIONS_EDIT = 'default_sso_permissions_edit';
export const DEFAULT_SSO_PERMISSIONS_SHARE = 'default_sso_permissions_share';
export const DEFAULT_SSO_PERMISSIONS_DOWNLOAD = 'default_sso_permissions_download';
export const DEFAULT_SSO_PERMISSIONS_DELETE = 'default_sso_permissions_delete';

// Action label constants
export const EDIT = 'Edit';
export const DOWNLOAD = 'Download';
export const SHARE = 'Share';
export const ADD_TO_PLAYLIST = 'Add to Playlist';
export const DELETE = 'Delete';
export const RENAME = 'Rename';
export const CLEAR = 'Clear';
export const PREVIEW = 'Preview';
export const CREATE_GALLERY = 'Create Gallery';
export const ADD_TO_GALLERY = 'Add to Gallery';
export const DELETE_SELECTED = 'Delete Selected';
export const REMOVE_ITEM_FROM_GALL = 'Remove from Gallery';

// Metadata field slug constants
export const FD_NAME_SLUG = 'name';
export const FD_FILE_NAME_SLUG = 'filename';
export const FD_MEDIA_GROUP_SLUG = 'media_group';
export const FD_MIMETYPE_SLUG = 'mimetype';
export const FD_TC_SLUG = 'timecode';
export const FD_INPOINT_SLUG = 'inpoint';

// Permission slug constants
export const MANAGE_GALLERY_SLUG = 'manage_gallery';
export const CAN_EDIT_SLUG = 'can_edit';
export const CAN_READ_SLUG = 'can_read';
export const CAN_DOWNLOAD_SLUG = 'can_download';
export const CAN_DELETE_SLUG = 'can_delete';
export const CAN_SHARE_SLUG = 'can_share';
export const CAN_DOWNLOAD_GALLERY_SLUG = 'can_download_gallery';
export const DOWNLOAD_HIRES = 'download_hires';
export const HIRES_SLUG = 'hires';
export const ADD_TO_PLAYLIST_SLUG = 'can_add_to_playlist';
export const DELETE_SELECTED_SLUG = 'delete_selected';
export const ADD_TO_GALLERY_SLUG = 'add_to_gallery';
export const EDIT_GALLERY_SLUG = 'edit_gallery';
export const REMOVE_FROM_GALLERY_SLUG = 'remove_from_gallery';

// Page constants
export const LIBRARY_PAGE = 'library-page';
export const GALLERY_PAGE = 'gallery-page';
export const JOBS_PAGE = 'jobs-page';
export const UPLOAD_PAGE = 'upload-page';

// Media type constants
export const VIDEO = 'video';
export const MXF = 'mxf';
export const AUDIO = 'audio';
export const IMAGE = 'image';
export const PDF = 'pdf';

// OAuth configuration constants
export const OAUTH_PROVIDER_NAME = 'oauth_provider_name';
export const OAUTH_CLIENT_ID = 'oauth_client_id';
export const OAUTH_CLIENT_SECRET = 'oauth_client_secret';
export const OIDC_DISCOVERY_URL = 'oidc_discovery_url';

// Format status constants
export const PROXY_OFFLINE = 'Proxy Offline';
export const THUMBNAIL_OFFLINE = 'Thumbnail Offline';
export const MEDIA_ERROR = 'Error Loading Media';

// Download selected constants
export const DOWNLOAD_SELECTED = 'Download Selected';
export const DOWNLOAD_SELECTED_SLUG = 'download_selected';

// Action mapping types
const whiteIconStyle: CSSProperties = { color: 'currentColor' };

export const ACTION_RULES = {
  REQUIRES_SELECTED_ASSET: 'requires_selected_asset',
} as const;

// Menu group constants
export const MENU_GROUPS = {
  MANAGE: 'Manage',
  EXPORT: 'Export',
  ORGANIZE: 'Organize',
  DANGER: 'Danger Zone',
} as const;

export type MenuGroup = (typeof MENU_GROUPS)[keyof typeof MENU_GROUPS];

/** Display order for menu groups — Danger Zone always last */
export const MENU_GROUP_ORDER: MenuGroup[] = [
  MENU_GROUPS.MANAGE,
  MENU_GROUPS.EXPORT,
  MENU_GROUPS.ORGANIZE,
  MENU_GROUPS.DANGER,
];

// Action item interface
export interface ActionItem {
  label: string;
  icon: ReactElement;
  slug?: string;
  rules?: string[];
  disabled?: boolean;
  group?: MenuGroup;
}

interface CreateActionOptions {
  rules?: string[];
  group?: MenuGroup;
}

type IconComponent = typeof GetAppIcon;

const createAction = (
  label: string,
  IconComponent: IconComponent,
  slug?: string,
  options: CreateActionOptions = {},
): ActionItem => ({
  label,
  icon: <IconComponent style={whiteIconStyle} />,
  ...(slug && { slug }),
  ...(options.rules && { rules: options.rules }),
  ...(options.group && { group: options.group }),
});

export const USER_PERMISSIONS_TO_GALLERY_MULTI_ACTION_MAP: Record<string, ActionItem[]> = {
  [MANAGE_GALLERY_SLUG]: [
    createAction(DOWNLOAD, GetAppIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
    createAction(REMOVE_ITEM_FROM_GALL, Remove, REMOVE_FROM_GALLERY_SLUG, {
      rules: [ACTION_RULES.REQUIRES_SELECTED_ASSET],
      group: MENU_GROUPS.DANGER,
    }),
  ],
};

export const GALLERYS_ITEM_ACTION_MULTI_ACTION_MAP: Record<string, ActionItem> = {
  [CAN_DELETE_SLUG]: createAction(DELETE, DeleteIcon, CAN_DELETE_SLUG, { group: MENU_GROUPS.DANGER }),
  [CAN_DOWNLOAD_SLUG]: createAction(DOWNLOAD, DownloadIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
};

export const USER_PERMISSONS_TO_ACTIONS_MAP: Record<string, ActionItem[]> = {
  [MANAGE_GALLERY_SLUG]: [
    createAction(CREATE_GALLERY, CreateNewFolder, MANAGE_GALLERY_SLUG, { group: MENU_GROUPS.ORGANIZE }),
    createAction(ADD_TO_GALLERY, AddToPhotos, ADD_TO_GALLERY_SLUG, { group: MENU_GROUPS.ORGANIZE }),
  ],
};

export const USER_PERMISSONS_TO_PLAYLIST_ACTIONS_MAP: Record<string, ActionItem[]> = {
  [MANAGE_GALLERY_SLUG]: [
    createAction(CREATE_GALLERY, CreateNewFolder, MANAGE_GALLERY_SLUG, { group: MENU_GROUPS.ORGANIZE }),
  ],
};

export const SHARED_LINK_INFO_ACTIONS: Record<string, ActionItem[]> = {
  [LINK_INFO_DOWNLOADABLE]: [
    createAction(DOWNLOAD, DownloadIcon, LINK_INFO_DOWNLOADABLE, { group: MENU_GROUPS.EXPORT }),
  ],
};

export const PERMISSONS_TO_ACTIONS_MAP: Record<string, ActionItem | ActionItem[]> = {
  [CAN_EDIT_SLUG]: [createAction(EDIT, EditNoteIcon, CAN_EDIT_SLUG, { group: MENU_GROUPS.MANAGE })],
  [CAN_DELETE_SLUG]: createAction(DELETE, DeleteIcon, CAN_DELETE_SLUG, { group: MENU_GROUPS.DANGER }),
  [CAN_DOWNLOAD_SLUG]: createAction(DOWNLOAD, DownloadIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
  [HIRES_SLUG]: createAction(HIRES_REQUEST, DownloadIcon, HIRES_SLUG, { group: MENU_GROUPS.EXPORT }),
  [CAN_SHARE_SLUG]: createAction(SHARE, ShareIcon, CAN_SHARE_SLUG, { group: MENU_GROUPS.MANAGE }),
  [MANAGE_GALLERY_SLUG]: [
    createAction(ADD_TO_GALLERY, AddToPhotos, ADD_TO_GALLERY_SLUG, { group: MENU_GROUPS.ORGANIZE }),
  ],
};

export const GALLERYS_ITEM_ACTION_MAP: Record<string, ActionItem> = {
  [CAN_EDIT_SLUG]: createAction(EDIT, EditNoteIcon, CAN_EDIT_SLUG, { group: MENU_GROUPS.MANAGE }),
  [CAN_DELETE_SLUG]: createAction(DELETE, DeleteIcon, CAN_DELETE_SLUG, { group: MENU_GROUPS.DANGER }),
  [CAN_DOWNLOAD_SLUG]: createAction(DOWNLOAD, DownloadIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
  [HIRES_SLUG]: createAction(HIRES_REQUEST, DownloadIcon, HIRES_SLUG, { group: MENU_GROUPS.EXPORT }),
  [CAN_SHARE_SLUG]: createAction(SHARE, ShareIcon, CAN_SHARE_SLUG, { group: MENU_GROUPS.MANAGE }),
  [MANAGE_GALLERY_SLUG]: createAction(REMOVE_ITEM_FROM_GALL, Remove, REMOVE_FROM_GALLERY_SLUG, {
    group: MENU_GROUPS.DANGER,
  }),
};

export const GALLERY_TYPE_ITEM_ACTION_MAP: Record<string, ActionItem[]> = {
  [MANAGE_GALLERY_SLUG]: [
    createAction(EDIT, EditNoteIcon, CAN_EDIT_SLUG, { group: MENU_GROUPS.MANAGE }),
    createAction(DELETE, DeleteIcon, CAN_DELETE_SLUG, { group: MENU_GROUPS.DANGER }),
    createAction(DOWNLOAD, DownloadIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
    createAction(SHARE, ShareIcon, CAN_SHARE_SLUG, { group: MENU_GROUPS.MANAGE }),
  ],
};

export const GLOBAL_MEDIA_PERMS_ACTIONS_MAP: Record<string, ActionItem> = {
  [CAN_DOWNLOAD_SLUG]: createAction(DOWNLOAD, DownloadIcon, CAN_DOWNLOAD_SLUG, { group: MENU_GROUPS.EXPORT }),
};

export const LIBRARY_ASSET_ACTIONS: ActionItem[] = [
  createAction(ADD_TO_PLAYLIST, PlaylistAddIcon, ADD_TO_PLAYLIST_SLUG, { group: MENU_GROUPS.ORGANIZE }),
];

export const ACTIONS: ActionItem[] = [
  createAction(EDIT, EditNoteIcon, undefined, { group: MENU_GROUPS.MANAGE }),
  createAction(DOWNLOAD, DownloadIcon, undefined, { group: MENU_GROUPS.EXPORT }),
  createAction(SHARE, ShareIcon, undefined, { group: MENU_GROUPS.MANAGE }),
  createAction(ADD_TO_PLAYLIST, PlaylistAddIcon, ADD_TO_PLAYLIST_SLUG, { group: MENU_GROUPS.ORGANIZE }),
  createAction(DELETE, DeleteIcon, undefined, { group: MENU_GROUPS.DANGER }),
];

export const GALLERY_ACTIONS: ActionItem[] = [
  createAction(EDIT, EditNoteIcon, undefined, { group: MENU_GROUPS.MANAGE }),
  createAction(DOWNLOAD, DownloadIcon, undefined, { group: MENU_GROUPS.EXPORT }),
  createAction(SHARE, ShareIcon, undefined, { group: MENU_GROUPS.MANAGE }),
];

export const PLAYLIST_ACTIONS: ActionItem[] = [
  createAction(DELETE, DeleteIcon, undefined, { group: MENU_GROUPS.DANGER }),
  createAction(RENAME, DriveFileRenameOutlineIcon, undefined, { group: MENU_GROUPS.MANAGE }),
  createAction(CLEAR, ClearIcon, undefined, { group: MENU_GROUPS.DANGER }),
  createAction(SHARE, ShareIcon, undefined, { group: MENU_GROUPS.MANAGE }),
  createAction(DOWNLOAD, DownloadIcon, undefined, { group: MENU_GROUPS.EXPORT }),
  createAction(PREVIEW, VisibilityIcon, undefined, { group: MENU_GROUPS.EXPORT }),
];

export const MULTI_ACTION: ActionItem[] = [
  createAction(ADD_TO_PLAYLIST, PlaylistAddIcon, ADD_TO_PLAYLIST_SLUG, { group: MENU_GROUPS.ORGANIZE }),
  createAction(DELETE_SELECTED, DeleteIcon, CAN_DELETE_SLUG, { group: MENU_GROUPS.DANGER }),
];

// Callback URL function
export const CALLBACK_URL = (): string => `${window.location.origin}/callback`;

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Are you sure you want to discard them?';

export const IMAGE_ZOOM_SCALE = {
  MAX: 8,
  MIN: 0.2,
} as const;

export const SAMPLE_DOWNLOAD_LINK = 'https://www.sharedfilespro.com/shared-files/38/?sample.pdf&download=1';
